import React from 'react';
import { Modal, Box, TextField, Button, Typography, CircularProgress, Alert, Grid, FormControlLabel, Switch, Divider } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Stack } from "@mui/system";
import { useMutation } from '@apollo/client';
import { CREATE_CAC_CLIENT } from "../../graphql/mutations/cacClient";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    maxHeight: '90vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

export default function ClientRegistrationModal({ open, handleClose, refetch }) {
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            companyName: '',
            email: '',
            phoneNumber: '',
            rcNumber: '',
            address: '',
            beebotCacPrice: '',
            beebotCacCompanyRegPrice: '',
            callbackUrl: '',
            vasApiKey: '',
            clientRef: '',
            active: true
        }
    });
    const [createCacClient, { data: cacClient, error, loading }] = useMutation(CREATE_CAC_CLIENT);

    const onSubmit = async (data) => {
        try {
            // Clean up the data - remove empty strings and convert numbers
            const processedData = {
                companyName: data.companyName,
                email: data.email,
                phoneNumber: data.phoneNumber || null,
                rcNumber: data.rcNumber || null,
                address: data.address || null,
                beebotCacPrice: data.beebotCacPrice ? parseFloat(data.beebotCacPrice) : null,
                beebotCacCompanyRegPrice: data.beebotCacCompanyRegPrice ? parseFloat(data.beebotCacCompanyRegPrice) : null,
                callbackUrl: data.callbackUrl || null,
                vasApiKey: data.vasApiKey || null,
                clientRef: data.clientRef || null,
                active: data.active
            };

            // Remove null values to avoid sending them to the backend
            Object.keys(processedData).forEach(key => {
                if (processedData[key] === null || processedData[key] === '') {
                    delete processedData[key];
                }
            });

            const result = await createCacClient({ variables: { input: processedData } });
            if (result.data && result.data.create) {
                reset();
                handleClose();
                refetch();
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Modal
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') return false;
                reset();
                handleClose();
            }}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
        >
            <Box sx={style}>
                <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
                    New Client Registration
                </Typography>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error.message}
                    </Alert>
                )}
                {cacClient && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Client ${cacClient.create.companyName} created successfully
                    </Alert>
                )}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Basic Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="companyName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Company name is required' }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Company Name *" 
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="email"
                                control={control}
                                defaultValue=""
                                rules={{ 
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'Invalid email address'
                                    }
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Email *" 
                                        type="email"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="phoneNumber"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField {...field} fullWidth label="Phone Number" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="rcNumber"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField {...field} fullWidth label="RC Number" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="address"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Address"
                                        multiline
                                        rows={3}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Pricing Configuration
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="beebotCacPrice"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="CAC Price (₦)" 
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="beebotCacCompanyRegPrice"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Company Registration Price (₦)" 
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        API Configuration
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                name="callbackUrl"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Callback URL" 
                                        placeholder="https://your-domain.com/callback"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="vasApiKey"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="VAS API Key" 
                                        type="password"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="clientRef"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField 
                                        {...field} 
                                        fullWidth 
                                        label="Client Reference" 
                                        placeholder="Unique client identifier"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Account Settings
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                name="active"
                                control={control}
                                defaultValue={true}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={field.onChange}
                                                color="primary"
                                            />
                                        }
                                        label="Active Account"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained" color="primary" disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : 'Create Client'}
                        </Button>
                        <Button onClick={() => { reset(); handleClose(); }} type="button" variant="outlined" color="primary">
                            Cancel
                        </Button>
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
}
